import { useTranslations } from "@jugl-web/utils";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSessionStorage } from "@jugl-web/utils/hooks/useStorage";
import { useEffectOnce } from "react-use";
import { SelectedPlanInfo } from "@web-src/modules/cx/components/CompletePurchaseDialog/components/SummaryStep/types";
import { ANONYMOUS_SESSION_PLAN_INFO } from "@jugl-web/utils/storage";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { ManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { ManageAccountsDrawerProvider } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerProvider";
import {
  EmailAccountProvider,
  InstagramAccountProvider,
  WhatsappAccountProvider,
  TwilioAccountProvider,
} from "@web-src/modules/client-support/pages/ClientSupportPage/providers";
import { WebChatBotAccountProvider } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useStartSessionRecording } from "@web-src/modules/common/utils/posthogSetup";
import { Button, InteractiveContainer, TabsLayout } from "@jugl-web/ui-kit";
import { ScrollablePage } from "@web-src/modules/common/components/ScrollablePage";
import { useSubscriptionV2 } from "../../providers/SubscriptionV2Provider";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { useCXSignupDialog } from "../../components/CXSignupDialog/useCXSignupDialog";
import { SetupAndTestTabContent } from "./components/SetupAndTestTabContent";
import { KnowledgeTabContent } from "./components/KnowledgeTabContent";
import { PreviewAndInstallTabContent } from "./components/PreviewAndInstallTabContent";
import { AutomateTabContent } from "./components/AutomateTabContent";
import { AgentSettingsDrawer } from "./components/AgentSettingsDrawer";

type TabId = "knowledge" | "automate" | "setupAndTest" | "previewAndInstall";

const CXAIAgentsPageContent: React.FC = () => {
  useStartSessionRecording();

  const { t } = useTranslations();
  const { entity } = useEntityProvider();
  const { isAnonymous, entityId: anonymousEntityId } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();
  const { isAdmin } = useUserRole();
  const { openSubscriptionDrawer, openPurchaseDialog, isFree } =
    useSubscriptionV2();
  const entityId = entity?.id || anonymousEntityId;
  const location = useLocation();
  const initialTab =
    (location.state as { defaultTab?: TabId } | null)?.defaultTab ??
    "knowledge";
  const [selectedTab, setSelectedTab] = useState<TabId>(initialTab);
  const [isAgentSettingsDrawerOpen, setIsAgentSettingsDrawerOpen] =
    useState(false);

  // Follow `defaultTab` whenever navigation supplies it (e.g. returning from an
  // integration OAuth flow). Guarded so clearing router state never forces a tab
  // change, and manual tab clicks (which don't touch `location.state`) win.
  useEffect(() => {
    const tab = (location.state as { defaultTab?: TabId } | null)?.defaultTab;
    if (tab) setSelectedTab(tab);
  }, [location.state]);

  const [selectedPlan, setSelectedPlan] = useSessionStorage<
    SelectedPlanInfo | undefined
  >(ANONYMOUS_SESSION_PLAN_INFO, undefined);

  useEffectOnce(() => {
    if (selectedPlan && !isAnonymous) {
      openPurchaseDialog(selectedPlan);
    }
  });

  const { isManageAccountDrawerOpen, openManageAccountDrawer } =
    useManageAccountsDrawer();

  return (
    <>
      <ScrollablePage>
        <TabsLayout
          breadcrumbProps={{
            items: [
              {
                title: t({
                  id: "cx-ai-agents-page.ai-agent",
                  defaultMessage: "AI Agent",
                }),
              },
            ],
            rightContent: (
              <InteractiveContainer
                className="flex h-8 items-center justify-center gap-2.5 rounded-[10px] px-4 transition-colors hover:bg-grey-200"
                onClick={() => setIsAgentSettingsDrawerOpen(true)}
              >
                <SettingsIcon />
                <span className="font-secondary text-sm font-medium text-dark">
                  {t({
                    id: "cx-ai-agents-page.general-ai-settings",
                    defaultMessage: "General AI settings",
                  })}
                </span>
              </InteractiveContainer>
            ),
          }}
          selectedTab={selectedTab}
          tabs={[
            {
              id: "knowledge",
              title: t({
                id: "cx-ai-agents-page.add-knowledge",
                defaultMessage: "Add Knowledge",
              }),
              number: 1,
              onClick: () => setSelectedTab("knowledge"),
            },
            {
              id: "setupAndTest",
              title: t({
                id: "cx-ai-agents-page.setup-and-test",
                defaultMessage: "Setup & Test",
              }),
              number: 2,
              onClick: () => setSelectedTab("setupAndTest"),
            },
            {
              id: "automate",
              title: t({
                id: "cx-ai-agents-page.automate",
                defaultMessage: "Automate",
              }),
              number: 3,
              onClick: () => setSelectedTab("automate"),
            },
            {
              id: "previewAndInstall",
              title: t({
                id: "cx-ai-agents-page.preview-and-install",
                defaultMessage: "Preview & Install",
              }),
              number: 4,
              onClick: () => setSelectedTab("previewAndInstall"),
            },
          ]}
          rightContent={
            (isAnonymous || (isAdmin && isFree)) && (
              <div className="flex items-center gap-4">
                <Button
                  onClick={() =>
                    openSubscriptionDrawer({
                      onSelectPlan: (planInfo) => {
                        if (isAnonymous) {
                          setSelectedPlan(planInfo);
                          cxSignupDialog?.open();
                          return;
                        }
                        openPurchaseDialog(planInfo);
                      },
                    })
                  }
                  variant="secondary"
                  iconEnd={<PricingIcon />}
                >
                  {t({
                    id: "cx-ai-agents-page.see-plans",
                    defaultMessage: "See plans",
                  })}
                </Button>
                {isAnonymous && (
                  <Button
                    onClick={() => cxSignupDialog?.open()}
                    className="w-[200px]"
                    iconEnd={<ArrowIcon />}
                  >
                    {t({
                      id: "cx-ai-agents-page.sign-up-for-free",
                      defaultMessage: "Sign Up for free",
                    })}
                  </Button>
                )}
              </div>
            )
          }
        >
          {selectedTab === "knowledge" ? (
            <KnowledgeTabContent entityId={entityId} />
          ) : selectedTab === "automate" ? (
            <AutomateTabContent entityId={entityId} />
          ) : selectedTab === "setupAndTest" ? (
            <SetupAndTestTabContent
              entityId={entityId}
              onAddAutomation={() => setSelectedTab("automate")}
            />
          ) : selectedTab === "previewAndInstall" ? (
            <PreviewAndInstallTabContent entityId={entityId} />
          ) : null}
        </TabsLayout>
      </ScrollablePage>
      <ManageAccountsDrawer
        isOpen={!!isManageAccountDrawerOpen}
        onRequestClose={() => openManageAccountDrawer(undefined)}
        initialScreen={isManageAccountDrawerOpen}
      />
      <AgentSettingsDrawer
        entityId={entityId}
        isOpen={isAgentSettingsDrawerOpen}
        onRequestClose={() => setIsAgentSettingsDrawerOpen(false)}
      />
    </>
  );
};

export const CXAIAgentsPage: React.FC = () => {
  return (
    <ManageAccountsDrawerProvider>
      <WhatsappAccountProvider>
        <InstagramAccountProvider>
          <WebChatBotAccountProvider>
            <EmailAccountProvider>
              <TwilioAccountProvider>
                <CXAIAgentsPageContent />
              </TwilioAccountProvider>
            </EmailAccountProvider>
          </WebChatBotAccountProvider>
        </InstagramAccountProvider>
      </WhatsappAccountProvider>
    </ManageAccountsDrawerProvider>
  );
};

export default CXAIAgentsPage;
